// src/pages/admin/TenantsPage.jsx
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { superAdminServices as svc } from '../../api/services'

const STATUS_COLORS = {
  active:    { bg: '#D1FAE5', text: '#065F46' },
  trial:     { bg: '#FEF3C7', text: '#92400E' },
  suspended: { bg: '#FEE2E2', text: '#991B1B' },
  expired:   { bg: '#F3F4F6', text: '#6B7280' },
}
const PLAN_COLORS = {
  starter:    '#6B7280',
  growth:     '#3B82F6',
  enterprise: '#8B5CF6',
}

export function TenantsPage() {
  const navigate = useNavigate()
  const [tenants, setTenants] = useState([])
  const [stats, setStats]     = useState(null)
  const [search, setSearch]   = useState('')
  const [filter, setFilter]   = useState({ status: '', plan: '' })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      svc.listTenants({ search, ...filter }),
      svc.tenantStats(),
    ]).then(([tRes, sRes]) => {
      setTenants(tRes.data?.data?.data ?? [])
      setStats(sRes.data?.data ?? {})
    }).finally(() => setLoading(false))
  }, [search, filter])

  const Stat = ({ value, label, color }) => (
    <div style={{ background: '#fff', borderRadius: 14, border: '1.5px solid #F0F0F0', padding: '16px 20px', textAlign: 'center' }}>
      <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1.6rem', color: color || '#FF3D7F' }}>{value}</div>
      <div style={{ fontSize: '0.75rem', color: '#9CA3AF', fontWeight: 600, marginTop: 4 }}>{label}</div>
    </div>
  )

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.6rem', color: '#1A1A2E', margin: 0 }}>Tenant Management</h1>
          <p style={{ color: '#9CA3AF', margin: '4px 0 0', fontSize: '0.88rem' }}>All marketplace tenants on the platform</p>
        </div>
        <button onClick={() => navigate('/admin/tenants/provision')}
          style={{ background: '#FF3D7F', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
          + Provision Tenant
        </button>
      </div>

      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginBottom: 24 }}>
          <Stat value={stats.total}  label="Total Tenants" />
          <Stat value={stats.active} label="Active" color="#10B981" />
          <Stat value={stats.trial}  label="On Trial" color="#F59E0B" />
          <Stat value={`₹${(stats.mrr / 1000).toFixed(0)}K`} label="Monthly MRR" color="#FF3D7F" />
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tenants…"
          style={{ flex: 1, minWidth: 200, padding: '9px 14px', border: '1.5px solid #E5E7EB', borderRadius: 10, fontSize: '0.85rem', outline: 'none' }} />
        {['', 'active', 'trial', 'suspended', 'expired'].map(s => (
          <button key={s} onClick={() => setFilter(f => ({ ...f, status: s }))}
            style={{ padding: '8px 14px', borderRadius: 10, border: `1.5px solid ${filter.status === s ? '#FF3D7F' : '#E5E7EB'}`, background: filter.status === s ? '#FF3D7F15' : '#fff', color: filter.status === s ? '#FF3D7F' : '#6B7280', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}>
            {s || 'All'}
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #F0F0F0', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}>Loading tenants…</div>
        ) : tenants.map((t, i) => {
          const sc = STATUS_COLORS[t.status] || STATUS_COLORS.expired
          return (
            <div key={t.id} onClick={() => navigate(`/admin/tenants/${t.id}`)}
              style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px', borderBottom: i < tenants.length - 1 ? '1px solid #F0F0F0' : 'none', cursor: 'pointer', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = '#FAFAFA'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              <div style={{ width: 36, height: 36, borderRadius: 10, background: `${PLAN_COLORS[t.plan] || '#6B7280'}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.85rem', color: PLAN_COLORS[t.plan] || '#6B7280', flexShrink: 0 }}>
                {t.name?.[0]?.toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1A1A2E' }}>{t.name}</div>
                <div style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>{t.slug}.fabrinza.com</div>
              </div>
              <span style={{ background: sc.bg, color: sc.text, padding: '3px 10px', borderRadius: 8, fontSize: '0.7rem', fontWeight: 700 }}>{t.status}</span>
              <span style={{ background: `${PLAN_COLORS[t.plan] || '#6B7280'}15`, color: PLAN_COLORS[t.plan] || '#6B7280', padding: '3px 10px', borderRadius: 8, fontSize: '0.7rem', fontWeight: 700 }}>{t.plan}</span>
              <div style={{ fontSize: '0.82rem', color: '#6B7280', minWidth: 100, textAlign: 'right' }}>₹{Number(t.monthly_fee).toLocaleString()}/mo</div>
              <span style={{ color: '#D1D5DB' }}>›</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// src/pages/admin/TenantProvision.jsx
export function TenantProvision() {
  const navigate = useNavigate()
  const [form, setForm] = useState({ name: '', slug: '', email: '', admin_name: '', plan: 'starter', billing_model: 'subscription', primary_color: '#FF3D7F', secondary_color: '#FF8500' })
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState('')

  const autoSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 30)

  const submit = async () => {
    setSaving(true); setError('')
    try {
      await svc.provisionTenant(form)
      navigate('/admin/tenants')
    } catch (e) {
      setError(e.response?.data?.message || 'Provisioning failed')
    } finally { setSaving(false) }
  }

  const Field = ({ label, hint, children }) => (
    <div style={{ marginBottom: 18 }}>
      <label style={{ display: 'block', fontWeight: 700, fontSize: '0.8rem', color: '#374151', marginBottom: 6 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: '0.72rem', color: '#9CA3AF', marginTop: 4 }}>{hint}</div>}
    </div>
  )

  const Input = ({ fkey, ...p }) => (
    <input value={form[fkey]} onChange={e => { setForm(f => ({ ...f, [fkey]: e.target.value })) }} {...p}
      style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #E5E7EB', borderRadius: 10, fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box' }} />
  )

  const BILLING_OPTS = [
    { value: 'subscription', label: 'Option 2 — Subscription', desc: 'Fixed monthly fee. Best for most tenants.' },
    { value: 'revenue_share', label: 'Option 1 — Revenue Share', desc: '0 monthly fee, % of GMV. For government/NGO clients.' },
    { value: 'hybrid', label: 'Option 3 — Hybrid (Future)', desc: 'Monthly fee + reduced transaction %. Reserved for Phase 2.' },
  ]

  return (
    <div style={{ maxWidth: 680 }}>
      <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: '0.85rem', marginBottom: 16 }}>← Back</button>
      <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.6rem', color: '#1A1A2E', margin: '0 0 6px' }}>Provision New Tenant</h1>
      <p style={{ color: '#9CA3AF', margin: '0 0 28px', fontSize: '0.88rem' }}>Creates tenant, admin user, subdomain, and sends welcome email in under 30 seconds.</p>

      <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #F0F0F0', padding: '28px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Marketplace Name">
            <Input fkey="name" placeholder="Rajasthan Textiles" onBlur={e => { if (!form.slug) setForm(f => ({ ...f, slug: autoSlug(e.target.value) })) }} />
          </Field>
          <Field label="Slug" hint={`Will be: ${form.slug || 'yourslug'}.fabrinza.com`}>
            <Input fkey="slug" placeholder="rajasthantextiles" />
          </Field>
          <Field label="Admin Name">
            <Input fkey="admin_name" placeholder="Priya Sharma" />
          </Field>
          <Field label="Admin Email">
            <Input fkey="email" placeholder="admin@rajasthantextiles.com" />
          </Field>
        </div>

        <Field label="Plan">
          <select value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))}
            style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #E5E7EB', borderRadius: 10, fontSize: '0.88rem', outline: 'none' }}>
            <option value="starter">Starter — ₹15,000/mo (50 suppliers, 500 products)</option>
            <option value="growth">Growth — ₹45,000/mo (500 suppliers, custom domain)</option>
            <option value="enterprise">Enterprise — ₹1,50,000/mo (unlimited, API, dedicated support)</option>
          </select>
        </Field>

        <Field label="Billing Model">
          {BILLING_OPTS.map(o => (
            <label key={o.value} style={{ display: 'flex', gap: 10, padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${form.billing_model === o.value ? '#FF3D7F' : '#E5E7EB'}`, marginBottom: 8, cursor: 'pointer', background: form.billing_model === o.value ? '#FFF0F5' : '#fff' }}>
              <input type="radio" value={o.value} checked={form.billing_model === o.value} onChange={e => setForm(f => ({ ...f, billing_model: e.target.value }))} />
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1A1A2E' }}>{o.label}</div>
                <div style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>{o.desc}</div>
              </div>
            </label>
          ))}
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Primary Colour">
            <div style={{ display: 'flex', gap: 10 }}>
              <input type="color" value={form.primary_color} onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))} style={{ width: 44, height: 40, border: 'none', cursor: 'pointer', borderRadius: 8 }} />
              <Input fkey="primary_color" />
            </div>
          </Field>
          <Field label="Secondary Colour">
            <div style={{ display: 'flex', gap: 10 }}>
              <input type="color" value={form.secondary_color} onChange={e => setForm(f => ({ ...f, secondary_color: e.target.value }))} style={{ width: 44, height: 40, border: 'none', cursor: 'pointer', borderRadius: 8 }} />
              <Input fkey="secondary_color" />
            </div>
          </Field>
        </div>

        {error && <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '10px 14px', borderRadius: 8, fontSize: '0.85rem', marginBottom: 16 }}>{error}</div>}

        {/* Preview */}
        <div style={{ background: form.primary_color, borderRadius: 12, padding: '16px 20px', marginBottom: 20, textAlign: 'center' }}>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: '1rem' }}>{form.name || 'Marketplace Name'}</div>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem', marginTop: 4 }}>{form.slug || 'slug'}.fabrinza.com</div>
        </div>

        <button onClick={submit} disabled={saving || !form.name || !form.email || !form.slug || !form.admin_name}
          style={{ width: '100%', background: '#FF3D7F', color: '#fff', border: 'none', borderRadius: 10, padding: '12px', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? '⏳ Provisioning (up to 30s)…' : '🚀 Provision Tenant'}
        </button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// src/pages/admin/TenantDetail.jsx
export function TenantDetail() {
  const { id } = require('react-router-dom').useParams()
  const navigate = useNavigate()
  const [tenant, setTenant] = useState(null)
  const [suspending, setSuspending] = useState(false)

  useEffect(() => { svc.getTenant(id).then(r => setTenant(r.data?.data ?? r.data)) }, [id])

  const suspend = async () => {
    const reason = prompt('Reason for suspension:')
    if (!reason) return
    setSuspending(true)
    await svc.suspendTenant(id, { reason })
    setTenant(t => ({ ...t, status: 'suspended' }))
    setSuspending(false)
  }

  const unsuspend = async () => {
    await svc.unsuspendTenant(id)
    setTenant(t => ({ ...t, status: 'active' }))
  }

  const impersonate = async () => {
    const r = await svc.impersonateTenant(id)
    const { token, tenant_url } = r.data?.data ?? {}
    window.open(`${tenant_url}/tenant/auth?token=${token}`, '_blank')
  }

  if (!tenant) return <div style={{ color: '#9CA3AF', padding: 40 }}>Loading…</div>

  const sc = { active: '#10B981', trial: '#F59E0B', suspended: '#DC2626', expired: '#9CA3AF' }

  return (
    <div style={{ maxWidth: 800 }}>
      <button onClick={() => navigate('/admin/tenants')} style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', fontSize: '0.85rem', marginBottom: 16 }}>← All Tenants</button>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: tenant.primary_color || '#FF3D7F', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: '1.2rem' }}>
            {tenant.name?.[0]}
          </div>
          <div>
            <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.4rem', color: '#1A1A2E', margin: 0 }}>{tenant.name}</h1>
            <div style={{ fontSize: '0.8rem', color: '#9CA3AF' }}>{tenant.slug}.fabrinza.com · {tenant.plan} plan</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={impersonate} style={{ padding: '8px 16px', border: '1.5px solid #E5E7EB', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>👤 Impersonate</button>
          {tenant.status === 'suspended'
            ? <button onClick={unsuspend} style={{ padding: '8px 16px', border: 'none', borderRadius: 8, background: '#10B981', color: '#fff', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>✓ Unsuspend</button>
            : <button onClick={suspend} disabled={suspending} style={{ padding: '8px 16px', border: 'none', borderRadius: 8, background: '#DC2626', color: '#fff', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700 }}>⛔ Suspend</button>
          }
        </div>
      </div>

      {/* Stats grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        {[['Suppliers', tenant.usage?.suppliers], ['Products', tenant.usage?.products], ['Orders', tenant.usage?.orders], [`₹${(tenant.usage?.gmv / 100000 || 0).toFixed(1)}L`, 'GMV']].map(([v, l]) => (
          <div key={l} style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #F0F0F0', padding: '16px', textAlign: 'center' }}>
            <div style={{ fontWeight: 800, fontSize: '1.4rem', color: '#FF3D7F' }}>{v}</div>
            <div style={{ fontSize: '0.75rem', color: '#9CA3AF', marginTop: 4 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Details */}
      <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #F0F0F0', padding: '24px' }}>
        {[
          ['Status', <span style={{ fontWeight: 700, color: sc[tenant.status] }}>{tenant.status}</span>],
          ['Plan', tenant.plan],
          ['Monthly Fee', `₹${Number(tenant.monthly_fee).toLocaleString()}`],
          ['Billing Model', tenant.billing_model],
          ['Contact', tenant.contact_email],
          ['Created', new Date(tenant.created_at).toLocaleDateString()],
          ['Expires', tenant.plan_expires_at ? new Date(tenant.plan_expires_at).toLocaleDateString() : '—'],
        ].map(([label, value]) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #F0F0F0', fontSize: '0.85rem' }}>
            <span style={{ color: '#9CA3AF', fontWeight: 600 }}>{label}</span>
            <span style={{ color: '#1A1A2E', fontWeight: 500 }}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
